import { combineEpics, ofType } from 'redux-observable'
import { merge, of } from 'rxjs'
import { catchError, filter, map, mergeMap, switchMap, take, takeUntil } from 'rxjs/operators'

import { extractErrorMessage } from 'utils/ErrorMessages'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import {
  selectActualCompanyId,
  selectActualDefaultLanguage,
  selectPartner,
  selectSelfCompanyId,
} from 'containers/App/selectors'
import { companyFilterChange } from 'containers/PartnerOptions/actions'
import { selectCompanyFilter, selectCompanyLoader } from 'containers/PartnerOptions/selectors'
import { getScenarioSuccess } from 'containers/ScenarioForm/actions'
import {
  DELETE_SCENARIO_ASYNC as SMM_DELETE_SCENARIO_ASYNC,
  SAVE_SCENARIO_ASYNC,
} from 'containers/ScenarioForm/constants'
import { addToast } from 'containers/Toasts/actions'

import {
  COMPANY_FILTER_CHANGE,
  FETCH_SELECTED_COMPANY_DATA_ASYNC,
} from '../../PartnerOptions/constants'

import {
  copyScenarioError,
  copyScenarioSuccess,
  createNewCampaignFromScenarioError,
  createNewCampaignFromScenarioSuccess,
  deleteScenarioError,
  deleteScenarioSuccess,
  fetchScenariosError,
  fetchScenariosSuccess,
} from './actions'
import {
  CATEGORY_FILTER_CHANGE,
  COPY_SCENARIO_ASYNC,
  CREATE_CAMPAIGN_FROM_SCENARIO_ASYNC,
  CREATED_BY_FILTER_CHANGE,
  DELETE_SCENARIO_ASYNC,
  DUPLICATE_SCENARIO_ASYNC,
  LANGUAGES_FILTER_CHANGE,
  LEVEL_FILTER_CHANGE,
  PAGINATE_CHANGE,
  SCENARIOS_REQUEST_ASYNC,
  SEARCH_CHANGE,
  SORT_PROPERTY_CHANGE,
  TYPE_FILTER_CHANGE,
} from './constants'
import { selectDuplicateCompanyId, selectDuplicateDisabledReason } from './selectors'

const fetchScenariosEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      SCENARIOS_REQUEST_ASYNC.INITIATE,
      LANGUAGES_FILTER_CHANGE,
      TYPE_FILTER_CHANGE,
      LEVEL_FILTER_CHANGE,
      CATEGORY_FILTER_CHANGE,
      PAGINATE_CHANGE,
      SEARCH_CHANGE,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
      DELETE_SCENARIO_ASYNC.SUCCESS,
      SMM_DELETE_SCENARIO_ASYNC.SUCCESS,
      SAVE_SCENARIO_ASYNC.SUCCESS,
      COPY_SCENARIO_ASYNC.SUCCESS,
      SORT_PROPERTY_CHANGE,
      CREATED_BY_FILTER_CHANGE
    ),
    filter((action) => {
      if (
        action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS ||
        action.type === SMM_DELETE_SCENARIO_ASYNC.SUCCESS ||
        action.type === SAVE_SCENARIO_ASYNC.SUCCESS ||
        action.type === COPY_SCENARIO_ASYNC.SUCCESS
      )
        return matchURL(URL_CONST.awarenessCenter.scenarios)
      else return true
    }),
    switchMap(() =>
      api.awarenessCenter
        .getScenarios(
          state$.value.awarenessCenter.scenarios.page || 1,
          state$.value.awarenessCenter.scenarios.sortProperty,
          state$.value.awarenessCenter.scenarios.categoryFilter
            ?.map((elem) => elem.label)
            .join(',') || 0,
          state$.value.awarenessCenter.scenarios.languagesFilter
            .map((elem) => elem.value)
            .join(','),
          state$.value.awarenessCenter.scenarios.typeFilter?.map((elem) => elem.value).join(','),
          state$.value.awarenessCenter.scenarios.levelFilter?.map((elem) => elem.value).join(','),
          state$.value.awarenessCenter.scenarios.createdByFilter
            ?.map((elem) => elem.value)
            .join(','),
          state$.value.awarenessCenter.scenarios.search,
          state$.value.partnerOptions.selectedCompany?.value,
          !!state$.value.app.data.partner
        )
        .pipe(
          mergeMap((response) => of(fetchScenariosSuccess(response))),
          catchError((error) => of(epicsErrorHandler(fetchScenariosError, error))),
          takeUntil(action$.pipe(ofType(SCENARIOS_REQUEST_ASYNC.CANCEL)))
        )
    )
  )

const deleteScenarioEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DELETE_SCENARIO_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .deleteScenario(
          action.payload.id,
          state$.value.partnerOptions.selectedCompany?.value,
          !!state$.value.app.data.partner
        )
        .pipe(
          map((response) => deleteScenarioSuccess(response)),
          catchError((error) => of(epicsErrorHandler(deleteScenarioError, error))),
          takeUntil(action$.pipe(ofType(DELETE_SCENARIO_ASYNC.CANCEL)))
        )
    )
  )

export const duplicateScenarioEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(DUPLICATE_SCENARIO_ASYNC.INITIATE),
    switchMap((action) => {
      const prepare = () => {
        const state = state$.value
        const companyId = selectDuplicateCompanyId(state)
        if (!companyId || (action.payload.companyId && companyId !== action.payload.companyId)) {
          return of(
            DUPLICATE_SCENARIO_ASYNC.onError({
              response: {
                message:
                  selectDuplicateDisabledReason(state) || 'Select the requested company first',
              },
            })
          )
        }
        return api.awarenessCenter
          .prepareScenarioCopy(action.payload.id, action.payload.name, companyId)
          .pipe(
            mergeMap((draft) =>
              of(
                getScenarioSuccess(draft, selectActualDefaultLanguage(state$.value)),
                DUPLICATE_SCENARIO_ASYNC.onSuccess()
              )
            ),
            catchError((error) =>
              of(epicsErrorHandler(DUPLICATE_SCENARIO_ASYNC.onError, error, [400, 403, 404]))
            ),
            takeUntil(action$.pipe(ofType(DUPLICATE_SCENARIO_ASYNC.CANCEL, COMPANY_FILTER_CHANGE)))
          )
      }
      const state = state$.value
      const targetId = action.payload.companyId
      if (targetId !== undefined) {
        const partner = selectPartner(state)
        const target =
          targetId === selectSelfCompanyId(state)
            ? { id: targetId, name: 'My company' }
            : partner?.all_companies.find(({ id }) => id === targetId)
        if (!(targetId > 0) || !target)
          return of(
            DUPLICATE_SCENARIO_ASYNC.onError({
              response: { message: 'The requested company is not accessible.' },
            })
          )
        if (
          partner &&
          (selectCompanyFilter(state)?.value !== targetId || selectCompanyLoader(state))
        ) {
          return merge(
            action$.pipe(
              ofType(
                FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS,
                FETCH_SELECTED_COMPANY_DATA_ASYNC.ERROR
              ),
              // The company switch this triggers isn't scoped to this page. take(1) must
              // come first so the wait completes on the FIRST result regardless of route —
              // otherwise an off-route result would be filtered out but leave the stream
              // subscribed, letting a later result (e.g. after the user returns to
              // Scenarios) resume a stale duplicate. filter() after take(1) then silently
              // drops that first result when the user has already navigated away.
              take(1),
              filter(() => matchURL(URL_CONST.awarenessCenter.scenarios)),
              mergeMap((result) =>
                result.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.ERROR
                  ? of(DUPLICATE_SCENARIO_ASYNC.onError(result.error))
                  : prepare()
              )
            ),
            of(companyFilterChange({ value: target.id, label: target.name }))
          ).pipe(
            takeUntil(
              action$.pipe(
                ofType(DUPLICATE_SCENARIO_ASYNC.CANCEL, COMPANY_FILTER_CHANGE),
                filter(
                  (result) =>
                    result.type === DUPLICATE_SCENARIO_ASYNC.CANCEL ||
                    result.payload?.selectedCompany?.value !== targetId
                )
              )
            )
          )
        }
      }
      return prepare()
    })
  )

export const copyScenarioEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(COPY_SCENARIO_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .copyScenario(
          action.payload.id,
          action.payload.companiesIds,
          action.payload.copyName,
          selectActualCompanyId(state$.value)
        )
        .pipe(
          mergeMap(() =>
            of(
              copyScenarioSuccess(),
              addToast({ type: 'success', message: 'Templates copied successfully!' })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(copyScenarioError, error, [400, 403, 404]),
              addToast({ type: 'error', message: extractErrorMessage(error) })
            )
          ),
          takeUntil(action$.pipe(ofType(COPY_SCENARIO_ASYNC.CANCEL)))
        )
    )
  )

const duplicateErrorEpic = (action$) =>
  action$.pipe(
    ofType(DUPLICATE_SCENARIO_ASYNC.ERROR),
    map(({ error }) => addToast({ type: 'error', message: extractErrorMessage(error) }))
  )

const createNewCampaignFromScenarioEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CREATE_CAMPAIGN_FROM_SCENARIO_ASYNC.INITIATE),
    mergeMap((action) =>
      api.awarenessCenter
        .createNewCampaignFromScenario(
          action.payload.id,
          undefined,
          state$.value.partnerOptions.selectedCompany?.value,
          !!state$.value.app.data.partner
        )
        .pipe(
          map((response) => createNewCampaignFromScenarioSuccess(response)),
          catchError((error) => of(epicsErrorHandler(createNewCampaignFromScenarioError, error))),
          takeUntil(action$.pipe(ofType(CREATE_CAMPAIGN_FROM_SCENARIO_ASYNC.CANCEL)))
        )
    )
  )

export default combineEpics(
  fetchScenariosEpic,
  deleteScenarioEpic,
  copyScenarioEpic,
  duplicateScenarioEpic,
  duplicateErrorEpic,
  createNewCampaignFromScenarioEpic
)
